import React, { useState, useEffect } from 'react';
import { tenantService } from '../services/tenantService.ts';
import { formatCurrency, formatDate } from '../utils/formatters.ts';
import LoadingSpinner from '../components/shared/LoadingSpinner.tsx';
import ErrorMessage from '../components/shared/ErrorMessage.tsx';
import RecordSettlementModal from '../components/settlements/RecordSettlementModal.tsx';
import '../styles/Settlements.css';

const Settlements = () => {
	const [settlements, setSettlements] = useState<any[]>([]);
	const [expandedTenants, setExpandedTenants] = useState<Set<string>>(
		new Set()
	);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [showRecordModal, setShowRecordModal] = useState(false);
	const [currentPage, setCurrentPage] = useState(1);
	const ITEMS_PER_PAGE = 10;

	const refreshSettlements = async () => {
		setLoading(true);
		try {
			const data = await tenantService.getOutstandingSettlements();
			setSettlements(data);
			setError(null);
		} catch (err) {
			setError('Failed to load settlements');
			console.error('Error:', err);
		} finally {
			setLoading(false);
		}
	};

	const groupSettlementsByTenant = (settlements: any[]) => {
		return settlements.reduce((groups, settlement) => {
			const tenantId = settlement.tenant.id;
			if (!groups[tenantId]) {
				groups[tenantId] = {
					tenant: settlement.tenant,
					settlements: [],
				};
			}
			groups[tenantId].settlements.push(settlement);
			return groups;
		}, {});
	};

	const toggleTenant = (tenantId: string) => {
		const newExpanded = new Set(expandedTenants);
		if (newExpanded.has(tenantId)) {
			newExpanded.delete(tenantId);
		} else {
			newExpanded.add(tenantId);
		}
		setExpandedTenants(newExpanded);
	};

	const renderOutstandingCell = (amount: number) => {
		return (
			<td
				className={`amount-pending ${
					amount === 0 ? 'zero-amount' : ''
				}`}
			>
				{formatCurrency(amount)}
			</td>
		);
	};

	const getStatusClass = (status: string) => {
		switch (status.toLowerCase()) {
			case 'cleared':
				return 'status-paid';
			case 'pending':
				return 'status-unpaid';
			default:
				return '';
		}
	};

	useEffect(() => {
		refreshSettlements();
	}, []);

	if (loading) return <LoadingSpinner />;
	if (error) return <ErrorMessage message={error} />;

	// Pagination logic
	const paginatedSettlements = settlements.slice(
		(currentPage - 1) * ITEMS_PER_PAGE,
		currentPage * ITEMS_PER_PAGE
	);

	const totalPages = Math.ceil(settlements.length / ITEMS_PER_PAGE);

	const handlePageChange = (page: number) => {
		setCurrentPage(page);
	};

	return (
		<div className='settlements-page'>
			<div className='settlements-header'>
				<h2>Settlements</h2>
				<button
					className='btn-primary'
					onClick={() => setShowRecordModal(true)}
				>
					+ Record Settlement
				</button>
			</div>

			<div className='settlements-container'>
				{/* Desktop View */}
				<table className='settlements-table desktop-only'>
					<thead>
						<tr>
							<th>Date</th>
							<th>Tenant</th>
							<th>Property</th>
							<th>Total Amount</th>
							<th>Paid Amount</th>
							<th>Outstanding</th>
							<th>Status</th>
							<th>Notes</th>
						</tr>
					</thead>
					<tbody>
						{paginatedSettlements.map((settlement) => (
							<tr key={settlement.id}>
								<td>
									{formatDate(settlement.settlement_date)}
								</td>
								<td>{settlement.tenant.full_name}</td>
								<td>{settlement.property.name}</td>
								<td>
									{formatCurrency(settlement.total_amount)}
								</td>
								<td className='amount-paid'>
									{formatCurrency(settlement.paid_amount)}
								</td>
								<td className={`amount-pending`}>
									{formatCurrency(settlement.outstanding_amount)}
								</td>
								<td>
									<span
										className={`status-badge ${getStatusClass(
											settlement.status
										)}`}
									>
										<span className='status-dot'></span>
										{settlement.status}
									</span>
								</td>
								<td>
									{settlement.settlement_notes || '-'}
								</td>
							</tr>
						))}
					</tbody>
				</table>

				{/* Mobile View */}
				<div className='settlement-cards mobile-only'>
					{paginatedSettlements.map((settlement) => (
						<div key={settlement.id} className='settlement-card'>
							<div className='settlement-card-header'>
								<span className='settlement-card-name'>
									{settlement.tenant.full_name}
								</span>
								<span className='settlement-card-date'>
									{formatDate(settlement.settlement_date)}
								</span>
							</div>
							<div className='settlement-card-details'>
								<div className='settlement-detail-row'>
									<label>Property:</label>
									<span>{settlement.property.name}</span>
								</div>
								<div className='settlement-detail-row'>
									<label>Total Amount:</label>
									<span>{formatCurrency(settlement.total_amount)}</span>
								</div>
								<div className='settlement-detail-row'>
									<label>Paid:</label>
									<span className='amount-paid'>
										{formatCurrency(settlement.paid_amount)}
									</span>
								</div>
								<div className='settlement-detail-row'>
									<label>Outstanding:</label>
									<span className='amount-pending'>
										{formatCurrency(settlement.outstanding_amount)}
									</span>
								</div>
								<div className='settlement-detail-row'>
									<label>Status:</label>
									<span
										className={`status-badge ${getStatusClass(
											settlement.status
										)}`}
									>
										<span className='status-dot'></span>
										{settlement.status}
									</span>
								</div>
								{settlement.settlement_notes && (
									<div className='settlement-notes'>
										<label>Notes:</label>
										<p>{settlement.settlement_notes}</p>
									</div>
								)}
							</div>
						</div>
					))}
				</div>

				{settlements.length > ITEMS_PER_PAGE && (
					<div className='pagination'>
						<button
							onClick={() => handlePageChange(currentPage - 1)}
							disabled={currentPage === 1}
							className='btn-secondary'
						>
							Previous
						</button>
						<span className='page-info'>
							Page {currentPage} of {totalPages}
						</span>
						<button
							onClick={() => handlePageChange(currentPage + 1)}
							disabled={currentPage === totalPages}
							className='btn-secondary'
						>
							Next
						</button>
					</div>
				)}
			</div>

			{showRecordModal && (
				<RecordSettlementModal
					onClose={() => setShowRecordModal(false)}
					onSubmit={async (settlementData) => {
						try {
							// Remove the deboardTenant call since it's handled in the modal
							refreshSettlements();
							setShowRecordModal(false);
						} catch (error: any) {
							console.error('Error recording settlement:', error);
							alert(
								error.message || 'Failed to record settlement'
							);
						}
					}}
				/>
			)}
		</div>
	);
};

export default Settlements;
